import React, { useEffect, useState, useRef } from 'react';

export default function AboutPage() {
  const [ripped, setRipped] = useState(false);
  const sunburstRef = useRef(null);
  const backdropRef = useRef(null);
  const badgeRef = useRef(null);

  useEffect(() => {
    setTimeout(() => setRipped(true), 800);

    // Generate sunburst rays
    const sb = sunburstRef.current;
    for (let i = 0; i < 36; i++) {
      const ray = document.createElement('div');
      ray.className = 'about-sunburst-ray';
      ray.style.transform = `rotate(${i * 10}deg)`;
      ray.style.opacity = 0.4 + Math.random() * 0.5;
      ray.style.height = (15 + Math.random() * 30) + 'px';
      sb.appendChild(ray);
    }

    // God rays
    const bd = backdropRef.current;
    for (let i = 0; i < 12; i++) {
      const ray = document.createElement('div');
      ray.className = 'about-god-ray';
      ray.style.left = (10 + Math.random() * 80) + '%';
      ray.style.width = (1 + Math.random() * 3) + 'px';
      ray.style.animationDelay = Math.random() * 8 + 's';
      ray.style.animationDuration = (6 + Math.random() * 6) + 's';
      ray.style.opacity = 0.3 + Math.random() * 0.5;
      bd.appendChild(ray);
    }

    // Ascending particles
    const interval = setInterval(() => {
      const p = document.createElement('div');
      p.className = 'about-holy-particle';
      p.style.left = Math.random() * 100 + '%';
      p.style.bottom = '-10px';
      p.style.width = (1 + Math.random() * 3) + 'px';
      p.style.height = p.style.width;
      p.style.animationDuration = (4 + Math.random() * 6) + 's';
      bd.appendChild(p);
      setTimeout(() => p.remove(), 10000);
    }, 80);

    // Developer badge particles + lightning
    const badgeEl = badgeRef.current;
    const particleInterval = setInterval(() => {
      if (!badgeEl) return;
      const p = document.createElement('div');
      p.className = 'dev-particle';
      const dx = (Math.random() - 0.5) * 40;
      const dy = -(Math.random() * 50 + 10);
      p.style.setProperty('--dx', dx + 'px');
      p.style.setProperty('--dy', dy + 'px');
      p.style.left = (40 + Math.random() * 40) + 'px';
      p.style.top = (20 + Math.random() * 60) + 'px';
      p.style.background = Math.random() > 0.3 ? '#d4af37' : '#fff';
      badgeEl.querySelector('.badge-developer').appendChild(p);
      setTimeout(() => p.remove(), 1800);
    }, 20);

    function spawnLightning() {
      if (!badgeEl) return;
      const bolt = document.createElement('div');
      bolt.className = 'dev-lightning';
      const angle = Math.random() * 360;
      bolt.style.setProperty('--angle', angle + 'deg');
      bolt.style.transform = `rotate(${angle}deg)`;
      badgeEl.querySelector('.badge-developer').appendChild(bolt);
      requestAnimationFrame(() => bolt.classList.add('strike'));
      setTimeout(() => bolt.remove(), 200);
    }
    function scheduleLightning() {
      spawnLightning();
      setTimeout(scheduleLightning, 400 + Math.random() * 800);
    }
    scheduleLightning();

    return () => { clearInterval(interval); clearInterval(particleInterval); };
  }, []);

  return (
    <div className="about-page">
      {/* Rip intro */}
      <div className={`about-rip-intro ${ripped ? 'ripped' : ''}`}>
        <div className="about-rip-intro-top" />
        <div className="about-rip-intro-bottom" />
      </div>
      {ripped && <div className="about-holy-flash" />}

      {/* Torn frame */}
      <div className="about-rip-frame-top" />
      <div className="about-rip-frame-bottom" />

      {/* Animated holy backdrop */}
      <div className="about-holy-backdrop" ref={backdropRef}>
        <div className="about-sunburst" ref={sunburstRef} />
        <div className="about-sunburst-pulse" />
        <div className="about-divine-glow" />
        <div className="about-light-flare" />
      </div>

      {/* Content */}
      <div className={`about-content ${ripped ? 'visible' : ''}`}>
        <div className="about-char-class">⚔️ Paladin · Oath of Devotion</div>
        <h1 className="about-char-name">SIGURTYR</h1>
        <p className="about-char-title">"The light does not ask permission to shine."</p>

        <div className="about-badge-row" ref={badgeRef}>
          <div className="badge badge-developer">
            <div className="badge-ring"><div className="badge-inner" style={{ fontSize: '1.2rem', fontFamily: 'monospace', color: '#d4af37' }}>&lt;/&gt;</div></div>
            <div className="badge-label">DEVELOPER</div>
          </div>
        </div>

        <div className="about-section">
          <h3>📖 Before the Fall</h3>
          <p>Sigurtyr Haldorsson was born in Bryn Shander, the largest of the Ten-Towns of Icewind Dale — a place where the sun barely rises and men are measured by their word. His father, Haldor, was a respected shield-captain of the town militia, and from him Sigurtyr learned two things: a blade is only as true as the hand that wields it, and a man's honor is the only thing the cold cannot take.</p>
          <p>At seventeen, Sigurtyr swore his oath to Tyr, the Maimed God — not in a grand cathedral, but alone beneath the aurora, kneeling on frozen stone with blood on his knuckles from a fight he'd won defending a merchant caravan. No priest witnessed it. No congregation sang. Just a boy, the sky, and a promise: <em>I will be just. I will not look away.</em></p>
          <p>He rose quickly. By twenty-three he was a Sworn Sword of the Order of the Even Hand, a small but fierce chapter of Tyr's faithful operating out of Silverymoon. His closest companion was <strong>Edric Voss</strong> — a fellow paladin, sharp-tongued, brilliant, the kind of man who could talk a dragon into doubting its own fire. They were brothers in all but blood.</p>

          <h3 style={{ marginTop: '24px' }}>🗡️ The Betrayal</h3>
          <p>It was Edric who framed him.</p>
          <p>A caravan of refugees from Sundabar — women, children, elderly — was found slaughtered on the road between Everlund and Silverymoon. Every piece of evidence pointed to Sigurtyr: his blade stolen from his quarters, his cloak planted at the scene, testimony from a paid "survivor." Edric himself wept before the tribunal, claiming he'd seen Sigurtyr leave that night with "a darkness in his eyes."</p>
          <p>The Order did not deliberate long. Sigurtyr was stripped of his rank, his holy symbol melted before his face, and he was sentenced to the Pit of Echoes — a forgotten oubliette beneath the Spine of the World. A place where men are sent to be forgotten, not punished.</p>
          <p>He was twenty-five.</p>

          <h3 style={{ marginTop: '24px' }}>⛓️ The Pit</h3>
          <p>For three years, Sigurtyr lived in darkness. A cell carved from glacial stone, eight feet by six. No window. No light save what leaked beneath the iron door when the warden's torch passed once a day.</p>
          <p>In the cell beside his, separated by a crack in the stone, was an old man. <strong>Maerus</strong> — once a sage of Candlekeep, imprisoned decades ago for possessing texts the Zhentarim wanted buried. Maerus was blind, frail, and utterly at peace.</p>
          <p><em>"You suffer because you believe the world owes you justice,"</em> Maerus told him. <em>"It does not. Justice is not owed. It is built. By hands that refuse to tremble."</em></p>
          <p>Over three years, Maerus taught him the ancient discipline of the Stoics — not the suppression of feeling, but the mastery of response. That anger is a fire that consumes the vessel before it ever reaches the enemy. That the obstacle is the way.</p>
          <p>Maerus died in the third winter. Quietly. Between one breath and the next. But he had given Sigurtyr one final gift: the location of a structural weakness in the eastern wall. It would take months of patient work. A lesser man would have broken his hands trying to force it.</p>
          <p>Sigurtyr was no longer a lesser man.</p>

          <h3 style={{ marginTop: '24px' }}>🌅 The Emergence</h3>
          <p>He emerged into blinding snow on a moonless night, gaunt as a specter, with nothing but rags and a mind forged harder than adamantine. He did not rush toward vengeance. That was the old Sigurtyr.</p>
          <p>He spent two years rebuilding himself. His body. His connections. His understanding of what Edric had done and why — not mere jealousy, but something far darker. Edric had been turned by agents of Bane, the Black Hand. The massacre was a Banite operation, and Sigurtyr was the convenient scapegoat.</p>
          <p>Edric now sits as a high commander of the Order of the Even Hand. Respected. Beloved. Building his power on a foundation of bones.</p>

          <h3 style={{ marginTop: '24px' }}>🧠 The Philosophy</h3>
          <p>Sigurtyr does not hate Edric. That is what frightens people.</p>
          <p><em>"Pain is inevitable. Suffering is the story you tell yourself about the pain. I stopped telling stories."</em></p>
          <p><em>"I do not seek revenge. I seek correction. The world is out of balance. I am the counterweight."</em></p>
          <p><em>"Tyr did not abandon me in that cell. He was tempering me. You do not forge a blade by keeping it comfortable."</em></p>
          <p><em>"I will offer Edric the chance to confess. Once. What he does with that chance is his burden, not mine."</em></p>
        </div>

        <div className="about-section">
          <h3>⚔️ Built By</h3>
          <p>This entire platform — bot, dashboard, combat engine, AI assistant — was built by one developer with a vision: make D&D 5e combat feel cinematic, tactical, and alive. Every spell, every crit, every ember on this page was crafted with intention.</p>
        </div>

        <div className="about-footer">
          <span>Powered by <a href="https://dndlorewalker.com">Lorewalker</a> 🌐</span>
        </div>
      </div>
    </div>
  );
}
